'use client';
import Logo from '@/components/global/Logo';
import { Motion } from '@/components/global/utils/Motion';
import { GroqImage } from '@/helpers/Image';
import { GROQButton, GROQImage } from '@/sanity/lib/definitions';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { motion, useScroll, useTransform } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { useRef } from 'react';
import { useIsMobile } from '@/hooks/useIsMobile';
import { CTAButton } from '@/shadcn/ui/button';

interface IHeartRignsSection {
  image: GROQImage;
  imageMobile: GROQImage;
  title: any; // Assuming this is a PortableText block
  button: GROQButton;
  buttonMobile: GROQButton;
}

export default function HeartRignsSection({
  image,
  imageMobile,
  title,
  button,
  buttonMobile,
}: IHeartRignsSection) {
  const isMobile = useIsMobile();
  
  return (
    <section id="heart-rigns-section" className="pt-[9.62rem] pb-[7.62rem] lg:pt-[36.25rem] lg:pb-[30.38rem] lg:pl-[26.88rem] lg:pr-[26.25rem] overflow-clip">
      <div className="container flex flex-col lg:flex-row items-center gap-[4rem] lg:gap-[20.88rem]">
        <motion.div
          initial={{ clipPath: 'inset(0% 100% 0% 0%)' }}
          whileInView={{ clipPath: 'inset(0% 0% 0% 0%)' }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1] }}
          className="w-full h-[61rem] lg:h-[73.5rem] relative overflow-hidden"
        >
          {image && (
            <GroqImage
              src={isMobile ? imageMobile : image}
              alt="Common Questions Image"
              className="!w-full !h-full !object-cover lg:object-center scale-100 lg:scale-[122%]"
            />
          )}
        </motion.div>
        <motion.div
          initial={{ clipPath: 'inset(0% 100% 0% 0%)' }}
          whileInView={{ clipPath: 'inset(0% 0% 0% 0%)' }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1], delay: 0.3 }}
          className="flex flex-col gap-[5rem] lg:gap-[12rem] w-full lg:w-[55rem] flex-shrink-0"
        >
          <div className="flex flex-col gap-[4rem] lg:gap-[2rem] prose-headings:text-[4rem] lg:prose-headings:text-[6rem] prose-headings:text-[#F2F0EF] prose-headings:uppercase prose-headings:leading-[120%] prose-em:!font-[300] prose-p:text-[#F2F0EF]/[72%] prose-p:tracking-[0.1rem] lg:prose-p:tracking-normal">
            {title && (
              <PortableText value={title} components={richTextComponent} />
            )}
          </div>
          {button && (
            <CTAButton data={button} className="hidden lg:block" />
          )}
          {buttonMobile && (
            <CTAButton data={buttonMobile} className="lg:hidden" />
          )}
        </motion.div>
      </div>
    </section>
  );
}
